import { useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import { useScopedKey } from "@/lib/queryClient";
import { useScopeStore } from "@/stores/scope";
import { MatchEditModal } from "@/features/matches/components/MatchEditModal";
import type { Match } from "@/lib/types";
import { PageHeader } from "./components/PageHeader";
import { CoachMatchUploadModal } from "./components/CoachMatchUploadModal";

/**
 * Coach > Matches — list the active team's matches with create/upload
 * affordances (M12b).
 *
 * Reads `GET /api/matches?team_id=…` (scope auto-injected by `apiFetch`).
 * Coaches get the `match:write` capability for teams they're a member of
 * (granted in tenancy.py M12b); the tenancy guard on POST + upload-session
 * endpoints rejects cross-team writes, so coaches can ONLY create + upload
 * matches for the team currently in scope.
 *
 * Two CTAs on every row:
 *   - "Edit" → reopens the same `MatchEditModal` admin uses.
 *   - "Upload video" → opens an upload session modal that drives the
 *     existing chunked-upload API for the chosen slot.
 *
 * The page-level "Add match" button lives in the PageHeader actions
 * slot. New-match flow: open `MatchEditModal` with `match=null`, then
 * on save offer to go straight into upload (the saved match is passed
 * via the `onSaved` callback).
 */
export function CoachMatchesPage() {
  const teamId = useScopeStore((s) => s.teamId);
  const teamName = useScopeStore((s) => s.active?.team.name ?? "");
  const matchesQuery = useQuery({
    queryKey: useScopedKey("coach-matches"),
    queryFn: () => apiFetch<Match[]>("/api/matches"),
    enabled: !!teamId,
  });

  const [editTarget, setEditTarget] = useState<Match | "new" | null>(null);
  const [uploadTarget, setUploadTarget] = useState<Match | null>(null);
  // Track the most-recently-created match so we can offer to advance
  // straight into the upload flow once the modal closes (M11+ legacy
  // pattern — the coach's expected next step after "create match" is
  // "upload video").
  const pendingUpload = useRef<Match | null>(null);

  const matches = matchesQuery.data ?? [];
  const sorted = useMemo(() => {
    return [...matches].sort((a, b) => {
      const da = a.date || "";
      const db = b.date || "";
      if (da !== db) return db.localeCompare(da);
      return (b.updated_at ?? "").localeCompare(a.updated_at ?? "");
    });
  }, [matches]);

  if (!teamId) {
    return (
      <div className="flex flex-col gap-5" data-testid="coach-matches-page">
        <p
          className="text-ink-muted text-sm font-mono px-3 py-6"
          data-testid="coach-matches-no-team"
        >
          No team selected.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5" data-testid="coach-matches-page">
      <PageHeader
        title="Matches"
        description={
          <>
            {teamName ? `${teamName} · ` : ""}Create matches and upload video
            for your team.
          </>
        }
        actions={
          <button
            type="button"
            onClick={() => setEditTarget("new")}
            data-testid="coach-matches-add"
            className="rounded border border-accent/50 bg-accent/15 px-3 py-1.5 text-[0.65rem] font-display uppercase tracking-wider text-accent hover:bg-accent/25 focus:outline-none focus:ring-2 focus:ring-accent"
          >
            Add Match
          </button>
        }
      />

      {matchesQuery.isLoading ? (
        <p
          data-testid="coach-matches-loading"
          className="font-mono text-sm text-ink-muted"
        >
          Loading matches…
        </p>
      ) : matchesQuery.isError ? (
        <p
          data-testid="coach-matches-error"
          className="font-mono text-sm text-destructive"
        >
          Couldn&rsquo;t load matches.
        </p>
      ) : sorted.length === 0 ? (
        <p
          data-testid="coach-matches-empty"
          className="font-mono text-sm text-ink-muted"
        >
          No matches yet. Use{" "}
          <button
            type="button"
            onClick={() => setEditTarget("new")}
            className="text-accent underline underline-offset-2 hover:no-underline"
          >
            Add Match
          </button>{" "}
          to create the first one.
        </p>
      ) : (
        <ul
          data-testid="coach-matches-list"
          role="list"
          className="flex flex-col gap-2"
        >
          {sorted.map((match) => (
            <li key={match.id}>
              <CoachMatchRow
                match={match}
                onEdit={() => setEditTarget(match)}
                onUpload={() => setUploadTarget(match)}
              />
            </li>
          ))}
        </ul>
      )}

      {editTarget ? (
        <MatchEditModal
          match={editTarget === "new" ? null : editTarget}
          onSaved={(saved) => {
            // On a fresh create we remember the new match so the
            // close handler below can prompt for upload. On edit we
            // don't carry pending state — the user explicitly opens
            // upload from the row.
            if (editTarget === "new") pendingUpload.current = saved;
          }}
          onClose={() => {
            setEditTarget(null);
            const next = pendingUpload.current;
            pendingUpload.current = null;
            if (next) setUploadTarget(next);
          }}
        />
      ) : null}

      {uploadTarget ? (
        <CoachMatchUploadModal
          match={uploadTarget}
          onClose={() => setUploadTarget(null)}
        />
      ) : null}
    </div>
  );
}

function CoachMatchRow({
  match,
  onEdit,
  onUpload,
}: {
  match: Match;
  onEdit: () => void;
  onUpload: () => void;
}) {
  const slots = match.video_status ?? {};
  const ready = Object.values(slots).filter((s) => s === "ready").length;
  const transcoding = Object.values(slots).filter(
    (s) => s === "transcoding",
  ).length;
  const errored = Object.values(slots).filter((s) => s === "error").length;
  return (
    <article
      data-testid="coach-match-row"
      data-match-id={match.id}
      className="flex flex-col gap-2 rounded-lg border border-rule bg-surface p-3 sm:flex-row sm:items-center sm:justify-between"
    >
      <div className="flex flex-col gap-1 min-w-0">
        <p className="font-display text-sm text-ink truncate">
          {match.home_team || "—"} vs {match.away_team || "—"}
        </p>
        <p className="font-mono text-[0.65rem] uppercase tracking-mono text-ink-muted">
          {match.date || "no date"}
          {match.time ? ` · ${match.time}` : ""}
          {match.location ? ` · ${match.location}` : ""}
        </p>
        <p className="font-mono text-[0.6rem] uppercase tracking-mono text-ink-muted">
          <span data-testid="coach-match-row-ready">{ready} ready</span>
          {transcoding > 0 ? (
            <>
              {" · "}
              <span data-testid="coach-match-row-transcoding">
                {transcoding} transcoding
              </span>
            </>
          ) : null}
          {errored > 0 ? (
            <>
              {" · "}
              <span
                data-testid="coach-match-row-error"
                className="text-destructive"
              >
                {errored} error
              </span>
            </>
          ) : null}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onEdit}
          data-testid="coach-match-row-edit"
          className="rounded border border-rule px-2 py-1 text-[0.65rem] font-display uppercase tracking-wider text-ink-muted hover:text-ink"
        >
          Edit
        </button>
        <button
          type="button"
          onClick={onUpload}
          data-testid="coach-match-row-upload"
          className="rounded border border-accent/50 bg-accent/15 px-2 py-1 text-[0.65rem] font-display uppercase tracking-wider text-accent hover:bg-accent/25"
        >
          Upload video
        </button>
      </div>
    </article>
  );
}
